import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, EyeOff, Link2, Unlink, RefreshCw } from "lucide-react";

const field = "h-9 text-xs";
const lbl = "text-[11px] font-semibold text-slate-500 uppercase";

export const XeroSettings = ({ open, onOpenChange, onChanged }) => {
  const [cfg, setCfg] = useState(null);
  const [catalog, setCatalog] = useState({ revenue_accounts: [], tax_rates: [] });
  const [itemGroups, setItemGroups] = useState([]);
  const [mappings, setMappings] = useState({}); // group -> {account_code, travel_separate, travel_account_code}
  const [secret, setSecret] = useState("");
  const [revealId, setRevealId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, groups] = await Promise.all([api.xeroConfig(), api.itemGroups().catch(() => [])]);
      setCfg(c); setSecret("");
      setItemGroups((groups || []).map((g) => (typeof g === "string" ? g : g.name)).filter(Boolean));
      const mm = {};
      (c.account_mappings || []).forEach((m) => {
        mm[m.item_group] = { account_code: m.account_code || "", travel_separate: !!m.travel_separate, travel_account_code: m.travel_account_code || "" };
      });
      setMappings(mm);
      if (c.connected) api.xeroCatalog().then(setCatalog).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load Xero settings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setMap = (group, key, val) => setMappings((m) => ({
    ...m, [group]: { account_code: "", travel_separate: false, travel_account_code: "", ...(m[group] || {}), [key]: val },
  }));

  const save = async () => {
    setBusy(true);
    try {
      const account_mappings = Object.entries(mappings)
        .map(([item_group, m]) => ({ item_group, account_code: m.account_code || "", travel_separate: !!m.travel_separate, travel_account_code: m.travel_separate ? (m.travel_account_code || "") : "" }))
        .filter((m) => m.account_code || m.travel_separate);
      const body = {
        client_id: cfg.client_id, redirect_uri: cfg.redirect_uri,
        account_code: cfg.account_code, account_mode: cfg.account_mode || "single", account_mappings,
        tax_type_default: cfg.tax_type_default,
        tax_type_private: cfg.tax_type_private, payment_terms_days: Number(cfg.payment_terms_days) || 14,
        pay_bsb: cfg.pay_bsb, pay_account_number: cfg.pay_account_number,
        pay_account_name: cfg.pay_account_name, pay_notes: cfg.pay_notes,
      };
      if (secret.trim()) body.client_secret = secret.trim();
      const saved = await api.saveXeroConfig(body);
      setCfg(saved); setSecret("");
      toast.success("Xero settings saved");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true);
    try {
      if (secret.trim() || !cfg.has_secret) {
        await api.saveXeroConfig({ client_id: cfg.client_id, redirect_uri: cfg.redirect_uri,
          ...(secret.trim() ? { client_secret: secret.trim() } : {}) });
      }
      const { authorization_url } = await api.xeroConnect();
      window.location.href = authorization_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Xero connect"); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero? Draft invoices already pushed stay in Xero.")) return;
    setBusy(true);
    try { await api.xeroDisconnect(); toast.success("Disconnected from Xero"); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="xero-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            Xero Accounting
            {cfg?.connected
              ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="xero-connected-badge">Connected{cfg.tenant_name ? ` · ${cfg.tenant_name}` : ""}</Badge>
              : <Badge variant="outline" className="text-slate-500">Not connected</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading || !cfg ? (
          <div className="py-12 text-center text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Credentials */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">App credentials</p>
              <div>
                <Label className={lbl}>Client ID</Label>
                <div className="flex gap-2">
                  <Input type={revealId ? "text" : "password"} value={cfg.client_id || ""}
                    onChange={(e) => set("client_id", e.target.value)} className={`${field} font-mono`} data-testid="xero-client-id" />
                  <Button type="button" variant="outline" className="h-9 px-2" onClick={() => setRevealId((s) => !s)} data-testid="xero-reveal-id">
                    {revealId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label className={lbl}>Client Secret</Label>
                <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                  placeholder={cfg.has_secret ? "•••••••••• saved — enter a new value to replace" : "Enter client secret"}
                  className={`${field} font-mono`} data-testid="xero-client-secret" />
                <p className="text-[10px] text-slate-400 mt-1">Stored encrypted. It's never shown again — save a new value to change it.</p>
              </div>
              <div>
                <Label className={lbl}>Redirect URI (must match Xero app exactly)</Label>
                <Input value={cfg.redirect_uri || ""} onChange={(e) => set("redirect_uri", e.target.value)} className={`${field} font-mono`} data-testid="xero-redirect-uri" />
              </div>
              <div className="flex gap-2 pt-1">
                {cfg.connected ? (
                  <Button variant="outline" onClick={disconnect} disabled={busy} className="h-9 text-xs text-red-600 border-red-200" data-testid="xero-disconnect">
                    <Unlink className="h-3.5 w-3.5 mr-1" /> Disconnect
                  </Button>
                ) : (
                  <Button onClick={connect} disabled={busy} className="h-9 text-xs" style={{ backgroundColor: "#13b5ea" }} data-testid="xero-connect">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-3.5 w-3.5 mr-1" />} Connect to Xero
                  </Button>
                )}
                {cfg.connected && (
                  <Button variant="outline" onClick={connect} disabled={busy} className="h-9 text-xs" data-testid="xero-reconnect">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reconnect
                  </Button>
                )}
              </div>
            </div>

            {/* Posting config */}
            <div className="space-y-3 border-t pt-4">
              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Invoice posting</p>

              <div>
                <Label className={lbl}>How should sales map to Xero accounts?</Label>
                <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 mt-1" data-testid="xero-account-mode">
                  <button type="button" onClick={() => set("account_mode", "single")} data-testid="xero-mode-single"
                    className={`px-3 h-8 text-[11px] font-medium rounded ${(cfg.account_mode || "single") === "single" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                    All sales → one account code
                  </button>
                  <button type="button" onClick={() => set("account_mode", "item_group")} data-testid="xero-mode-group"
                    className={`px-3 h-8 text-[11px] font-medium rounded ${cfg.account_mode === "item_group" ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                    By item group
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={lbl}>{cfg.account_mode === "item_group" ? "Default revenue account code" : "Revenue account code"}</Label>
                  {catalog.revenue_accounts.length ? (
                    <Select value={cfg.account_code || ""} onValueChange={(v) => set("account_code", v)}>
                      <SelectTrigger className={field} data-testid="xero-account-code"><SelectValue placeholder="Select account" /></SelectTrigger>
                      <SelectContent>
                        {catalog.revenue_accounts.map((a) => (
                          <SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={cfg.account_code || ""} onChange={(e) => set("account_code", e.target.value)} placeholder="e.g. 200" className={field} data-testid="xero-account-code-manual" />
                  )}
                  {cfg.account_mode === "item_group" && <p className="text-[10px] text-slate-400 mt-0.5">Fallback for any item group not mapped below.</p>}
                </div>
                <div>
                  <Label className={lbl}>Payment terms (days)</Label>
                  <Input type="number" value={cfg.payment_terms_days} onChange={(e) => set("payment_terms_days", e.target.value)} className={field} data-testid="xero-terms" />
                </div>
                <div>
                  <Label className={lbl}>Tax type — NDIS (GST-free)</Label>
                  {catalog.tax_rates.length ? (
                    <Select value={cfg.tax_type_default || ""} onValueChange={(v) => set("tax_type_default", v)}>
                      <SelectTrigger className={field} data-testid="xero-tax-default"><SelectValue placeholder="Select tax" /></SelectTrigger>
                      <SelectContent>
                        {catalog.tax_rates.map((t) => (<SelectItem key={t.type} value={t.type} className="text-xs">{t.name} ({t.rate}%)</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (<Input value={cfg.tax_type_default || ""} onChange={(e) => set("tax_type_default", e.target.value)} placeholder="EXEMPTOUTPUT" className={field} data-testid="xero-tax-default-manual" />)}
                </div>
                <div>
                  <Label className={lbl}>Tax type — Private (+GST)</Label>
                  {catalog.tax_rates.length ? (
                    <Select value={cfg.tax_type_private || ""} onValueChange={(v) => set("tax_type_private", v)}>
                      <SelectTrigger className={field} data-testid="xero-tax-private"><SelectValue placeholder="Select tax" /></SelectTrigger>
                      <SelectContent>
                        {catalog.tax_rates.map((t) => (<SelectItem key={t.type} value={t.type} className="text-xs">{t.name} ({t.rate}%)</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (<Input value={cfg.tax_type_private || ""} onChange={(e) => set("tax_type_private", e.target.value)} placeholder="OUTPUT" className={field} data-testid="xero-tax-private-manual" />)}
                </div>
              </div>
              {!cfg.connected && <p className="text-[10px] text-amber-600">Connect to Xero to load your account & tax type lists.</p>}

              {/* Per-item-group revenue account codes */}
              {cfg.account_mode === "item_group" && (
              <div className="mt-2">
                <Label className={lbl}>Account codes per item group</Label>
                <p className="text-[10px] text-slate-400 mb-2">Leave a group on "Use default" to post it to the default code above. Tick "Travel items use a different code" to send Provider Travel &amp; Activity Based Transport within that group to a separate account.</p>
                {!cfg.connected ? (
                  <p className="text-[10px] text-amber-600">Connect to Xero to choose account codes.</p>
                ) : itemGroups.length === 0 ? (
                  <p className="text-[10px] text-slate-400">No item groups found.</p>
                ) : (
                  <div className="border rounded-md divide-y max-h-64 overflow-y-auto" data-testid="xero-group-mappings">
                    {itemGroups.map((g) => {
                      const m = mappings[g] || {};
                      return (
                        <div key={g} className="p-2 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-700 flex-1 truncate" title={g}>{g}</span>
                            <Select value={m.account_code || "__default__"} onValueChange={(v) => setMap(g, "account_code", v === "__default__" ? "" : v)}>
                              <SelectTrigger className="h-8 text-[11px] w-56" data-testid={`xero-group-acct-${g}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__default__" className="text-xs">Use default ({cfg.account_code || "—"})</SelectItem>
                                {catalog.revenue_accounts.map((a) => (<SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2 pl-1">
                            <Checkbox id={`trav-${g}`} checked={!!m.travel_separate} onCheckedChange={(v) => setMap(g, "travel_separate", !!v)} data-testid={`xero-group-travel-toggle-${g}`} />
                            <label htmlFor={`trav-${g}`} className="text-[11px] text-slate-500 cursor-pointer">Travel items use a different code</label>
                            {m.travel_separate && (
                              <Select value={m.travel_account_code || "__default__"} onValueChange={(v) => setMap(g, "travel_account_code", v === "__default__" ? "" : v)}>
                                <SelectTrigger className="h-7 text-[11px] w-52 ml-auto" data-testid={`xero-group-travel-acct-${g}`}><SelectValue placeholder="Travel account" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__default__" className="text-xs">Same as this group</SelectItem>
                                  {catalog.revenue_accounts.map((a) => (<SelectItem key={a.code} value={a.code} className="text-xs">{a.code} — {a.name}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Payment details */}
            <div className="space-y-3 border-t pt-4">
              <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">Payment details</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className={lbl}>Account name</Label><Input value={cfg.pay_account_name || ""} onChange={(e) => set("pay_account_name", e.target.value)} className={field} data-testid="xero-pay-name" /></div>
                <div><Label className={lbl}>BSB</Label><Input value={cfg.pay_bsb || ""} onChange={(e) => set("pay_bsb", e.target.value)} className={field} data-testid="xero-pay-bsb" /></div>
                <div><Label className={lbl}>Account number</Label><Input value={cfg.pay_account_number || ""} onChange={(e) => set("pay_account_number", e.target.value)} className={field} data-testid="xero-pay-acct" /></div>
              </div>
              <div>
                <Label className={lbl}>Invoice notes / payment instructions</Label>
                <Textarea value={cfg.pay_notes || ""} onChange={(e) => set("pay_notes", e.target.value)} rows={2} className="text-xs" data-testid="xero-pay-notes" />
              </div>
              <p className="text-[10px] text-slate-400">Bank details render on the Xero invoice via your Xero branding theme. Enter them here so they're captured with each run's config.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 text-xs" data-testid="xero-close">Close</Button>
          <Button onClick={save} disabled={busy || loading} className="h-9 text-xs" style={{ backgroundColor: "var(--brand)" }} data-testid="xero-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
